import React, { Component, useState } from 'react';
import { array, string, func } from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { lazyLoadWithDimensions } from '../../util/contextHelpers';
import { propTypes } from '../../util/types';
import { convertMoney, formatMoney } from '../../util/currency';
import { ensureListing } from '../../util/data';
import { richText } from '../../util/richText';
import { findOptionsForSelectFilter } from '../../util/search';
import { createSlug } from '../../util/urlHelpers';
import useMarketplaceConfigByLocation from '../../util/hooks/useMarketplaceConfigByLocation';
import { NamedLink, ResponsiveImage, IconReviewStar } from '../../components';
import get from 'lodash/get';
import { IconSpinner } from '../../components';

import css from './ListingCard.module.css';
import minBy from 'lodash/minBy';
import { useDispatch, useSelector } from 'react-redux';
import { updateFavoriteList } from '../../ducks/favorite.duck';
import { fetchFavoriteList } from '../../containers/FavoritePage/FavoritePage.duck';
import { parse } from '../../util/urlHelpers';
import FillHeartIcon from './FIllHeartIcon';
import NoneFillHeartIcon from './NoneFillHeartIcon';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, intl) => {
  const inLiveSite = useMarketplaceConfigByLocation.currentCountryCode === undefined;
  if (inLiveSite && price) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price && price.currency === useMarketplaceConfigByLocation.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const getCertificateInfo = (certificateOptions, key) => {
  return certificateOptions.find(c => c.key === key);
};

class ListingImage extends Component {
  render() {
    return <ResponsiveImage {...this.props} />;
  }
}
const LazyImage = lazyLoadWithDimensions(ListingImage, { loadAfterInitialRendering: 3000 });

const getCheapestService = services => {
  if (services.length === 0) return;
  return minBy(services, service => (service.price ? service.price.amount : null));
};
export const ListingCardComponent = props => {
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    filtersConfig,
    setActiveListing,
  } = props;
  const dispatch = useDispatch();
  const { updateFavoriteListInProgress } = useSelector(state => state.favorite);
  const { currentUser } = useSelector(state => state.user);
  const favoriteList = currentUser?.attributes?.profile?.privateData?.favoriteList?.own || [];
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const isLoadingHeartIcon = updateFavoriteListInProgress === id;
  const hasLoadingHeartIcon = updateFavoriteListInProgress ? true : false;
  const isFavorite = favoriteList.includes(id);

  const certificateOptions = findOptionsForSelectFilter('certificate', filtersConfig);
  const certificate = publicData
    ? getCertificateInfo(certificateOptions, publicData.certificate)
    : null;

  const unitTranslationKey = 'ListingCard.unitText';

  const reviewsText = intl.formatMessage({ id: 'ListingCard.reviews' });
  const reviewCount = get(listing, 'attributes.metadata.reviewCount', 0);
  const averageRating = get(listing, 'attributes.metadata.averageRating', 0) / 100;
  const services = get(listing, 'attributes.publicData.services', []);

  const isOneService = services.length === 1;
  const cheapestService = !isOneService ? getCheapestService(services) : services[0];

  const cheapestPrice = cheapestService && convertMoney(cheapestService.price);
  const { formattedPrice, priceTitle } = priceData(cheapestPrice, intl);

  const heartClasses = classNames(hasLoadingHeartIcon ? css.disableIcon : css.enableIcon);

  const onClickHeartIcon = async (e, id) => {
    e.preventDefault();
    if (!hasLoadingHeartIcon) {
      await dispatch(updateFavoriteList(id));
      const { page = 1 } = parse(window.location.search);
      if (window.location.pathname === '/favorite') {
        await dispatch(fetchFavoriteList(page));
      }
    }
  };

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <div
        className={css.threeToTwoWrapper}
        onMouseEnter={() => setActiveListing(currentListing.id)}
        onMouseLeave={() => setActiveListing(null)}
      >
        <div className={css.aspectWrapper}>
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={['landscape-crop', 'landscape-crop2x']}
            sizes={renderSizes}
          />
          <div className={heartClasses}>
            {isLoadingHeartIcon ? (
              <IconSpinner />
            ) : (
              <>
                {isFavorite ? (
                  <FillHeartIcon className={css.fillHeart} onClick={e => onClickHeartIcon(e, id)} />
                ) : (
                  <NoneFillHeartIcon onClick={e => onClickHeartIcon(e, id)} />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div className={css.info}>
        <div className={css.price}>
          <div className={css.priceValue} title={priceTitle}>
            {formattedPrice}
          </div>
          {!isOneService && (
            <div className={css.perUnit}>
              <FormattedMessage id={unitTranslationKey} />
            </div>
          )}
        </div>
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          <div className={css.certificateInfo}>
            {certificate && !certificate.hideFromListingInfo ? (
              <span>{certificate.label}</span>
            ) : null}
          </div>
        </div>
        <div className={css.reviews}>
          <div className={css.reviewWrapper}>
            <div className={css.reviewsStar}>
              <span>{`${averageRating}`}</span>
              <IconReviewStar rootClassName={css.star} />
            </div>
            <span>{`(${reviewCount} ${reviewsText})`}</span>
          </div>
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  filtersConfig: useMarketplaceConfigByLocation.custom.filters,
  setActiveListing: () => null,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  filtersConfig: array,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
